import React, { useState, useEffect } from 'react';
import { 
  X, Building2, MapPin, UserCheck, Lock, Save, Globe, Layers, Zap, CheckCircle2, 
  LayoutDashboard, Target, Wallet, FileSignature, FolderKanban, TrendingUp, 
  ClipboardList, Radio, Package, Calendar, Wrench, ShieldCheck, BarChart3, 
  Activity, FileText, CreditCard, Users, Key, Link2, Settings, PackageSearch,
  Check, Hash, ChevronDown
} from 'lucide-react';
import { Company, AppVertical, UserRole } from './types';
import { Badge } from './SharedComponents';

interface NewCompanyModalProps {
  onClose: () => void;
  onSubmit: (company: Company, manager: any) => void;
  initialPlan?: string | null;
}

const ALL_UNIT_MODULES = [
  { id: 'dashboard', label: 'Painel Geral', group: 'Dashboard', icon: LayoutDashboard },
  { id: 'leads', label: 'Leads CRM', group: 'Comercial', icon: Target },
  { id: 'estimates', label: 'Orçamentos', group: 'Comercial', icon: Wallet },
  { id: 'contracts', label: 'Contratos', group: 'Comercial', icon: FileSignature },
  { id: 'projects', label: 'Projetos', group: 'Comercial', icon: FolderKanban },
  { id: 'goals', label: 'Metas Vendas', group: 'Comercial', icon: TrendingUp },
  { id: 'orders', label: 'Ordens O.S.', group: 'Operação', icon: ClipboardList },
  { id: 'monitor', label: 'Radar Live', group: 'Operação', icon: Radio },
  { id: 'separation', label: 'Expedição', group: 'Operação', icon: Package },
  { id: 'calendar', label: 'Agenda', group: 'Operação', icon: Calendar },
  { id: 'maintenance', label: 'Oficina', group: 'Operação', icon: Wrench },
  { id: 'quality', label: 'IA Avarias', group: 'Operação', icon: ShieldCheck },
  { id: 'event-finance', label: 'Controladoria', group: 'Financeiro', icon: ShieldCheck },
  { id: 'event-profitability', label: 'Lucro Evento', group: 'Financeiro', icon: BarChart3 },
  { id: 'internal-finance', label: 'Fluxo Caixa', group: 'Financeiro', icon: Activity },
  { id: 'invoices', label: 'Faturas / NF', group: 'Financeiro', icon: FileText },
  { id: 'payments', label: 'Pagamentos', group: 'Financeiro', icon: CreditCard },
  { id: 'clients', label: 'Parceiros', group: 'Ativos & Config', icon: Users },
  { id: 'products', label: 'Inventário', group: 'Ativos & Config', icon: PackageSearch },
  { id: 'teams', label: 'Equipes', group: 'Ativos & Config', icon: Users },
  { id: 'users', label: 'Acessos', group: 'Ativos & Config', icon: Key },
  { id: 'integration', label: 'Conexões', group: 'Ativos & Config', icon: Link2 },
  { id: 'settings', label: 'Ajustes', group: 'Ativos & Config', icon: Settings },
];

export const NewCompanyModal = ({ onClose, onSubmit, initialPlan }: NewCompanyModalProps) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(() => ({
    name: '',
    cnpj: '',
    address: '',
    neighborhood: '',
    city: '',
    state: 'SP',
    zip: '',
    vertical: AppVertical.EVENTS,
    plan: (initialPlan?.replace('p-', '').toUpperCase() as Company['plan']) || 'PRO',
    managerName: '',
    email: '',
    password: '',
    whatsapp: '',
    authorizedModules: ALL_UNIT_MODULES.map(m => m.id)
  }));

  const toggleModule = (id: string) => {
    setFormData(prev => ({
      ...prev,
      authorizedModules: prev.authorizedModules.includes(id)
        ? prev.authorizedModules.filter(m => m !== id)
        : [...prev.authorizedModules, id]
    }));
  };

  const selectModulesByGroup = (group: string) => {
    const groupModuleIds = ALL_UNIT_MODULES.filter(m => m.group === group).map(m => m.id);
    const allInGroupSelected = groupModuleIds.every(id => formData.authorizedModules.includes(id));
    
    if (allInGroupSelected) {
      setFormData(prev => ({
        ...prev,
        authorizedModules: prev.authorizedModules.filter(id => !groupModuleIds.includes(id))
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        authorizedModules: [...new Set([...prev.authorizedModules, ...groupModuleIds])]
      }));
    }
  };

  const handleNextStep = () => {
    if (step === 1 && (!formData.name || !formData.cnpj)) return alert("Preencha os dados da empresa.");
    if (step === 3 && (!formData.managerName || !formData.email || !formData.password)) return alert("Preencha os dados do gestor.");
    
    setStep(prev => prev + 1);
  };

  const handleFinalSubmit = () => {
    // IMPORTANTE: Deixamos o ID vazio para o banco gerar o UUID automaticamente
    const newCompany: Company = {
      id: '', 
      name: formData.name,
      cnpj: formData.cnpj,
      address: formData.address,
      neighborhood: formData.neighborhood,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      managerName: formData.managerName,
      contactEmail: formData.email,
      whatsapp: formData.whatsapp,
      plan: formData.plan,
      vertical: formData.vertical,
      status: 'ACTIVE',
      mrr: formData.plan === 'START' ? 397 : formData.plan === 'PRO' ? 797 : 1497,
      payable: 0,
      receivable: 0,
      activeOrders: 0,
      usersCount: 1,
      lastSync: new Date().toISOString(),
      nfseEnabled: false,
      blocked: false,
      iaUsageCount: 0,
      equityValue: 0
    };

    const managerUser = {
      name: formData.managerName,
      email: formData.email,
      password: formData.password,
      role: UserRole.ADMIN,
      authorizedTabs: formData.authorizedModules
    };

    onSubmit(newCompany, managerUser);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (step < 4) {
        handleNextStep();
      }
    }
  };

  const groups = Array.from(new Set(ALL_UNIT_MODULES.map(m => m.group)));

  return (
    <div className="fixed inset-0 bg-secondary/95 backdrop-blur-md z-[150] flex items-center justify-center p-6" onKeyDown={handleKeyDown}>
      <div className="bg-white rounded-[50px] w-full max-w-5xl shadow-[0_40px_100px_rgba(0,0,0,0.5)] flex flex-col max-h-[92vh] animate-in zoom-in-95 duration-300 overflow-hidden border border-white/20">
        
        {/* Header */}
        <div className="p-10 bg-[#0f172a] text-white flex justify-between items-center shrink-0 relative">
           <div className="relative z-10 flex items-center gap-6">
              <div className="w-16 h-16 bg-primary rounded-[24px] flex items-center justify-center text-white shadow-xl shadow-primary/20">
                 <Zap size={32} />
              </div>
              <div>
                <h2 className="text-3xl font-black uppercase tracking-tighter italic text-white leading-none">Provisionar Unidade</h2>
                <div className="flex items-center gap-4 mt-4">
                   <StepIndicator num={1} label="Empresa" active={step === 1} done={step > 1} />
                   <StepIndicator num={2} label="Plano" active={step === 2} done={step > 2} />
                   <StepIndicator num={3} label="Gestor" active={step === 3} done={step > 3} />
                   <StepIndicator num={4} label="Módulos" active={step === 4} />
                </div>
              </div>
           </div>
          <button onClick={onClose} className="p-4 bg-white/5 hover:bg-red-500/20 hover:text-red-500 rounded-2xl transition-all relative z-10 active:scale-90"><X size={32} /></button>
        </div>
        
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-12 space-y-12 scrollbar-hide">
            {step === 1 && (
              <div className="space-y-10 animate-in slide-in-from-right-4 duration-500">
                 <section className="space-y-6">
                    <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] border-b border-gray-50 pb-3 flex items-center gap-2 italic">
                       <Building2 size={16} /> Identificação Corporativa
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <InputGroup label="Razão Social" value={formData.name} onChange={(v: string) => setFormData({...formData, name: v})} placeholder="Ex: Matriz Sorocaba Locações" />
                      <InputGroup label="CNPJ Operacional" value={formData.cnpj} onChange={(v: string) => setFormData({...formData, cnpj: v})} placeholder="00.000.000/0001-00" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Nicho / Vertical de Negócio</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                         <VerticalBtn label="Eventos" icon={Calendar} active={formData.vertical === AppVertical.EVENTS} onClick={() => setFormData({...formData, vertical: AppVertical.EVENTS})} />
                         <VerticalBtn label="Obras" icon={Building2} active={formData.vertical === AppVertical.CONSTRUCTION} onClick={() => setFormData({...formData, vertical: AppVertical.CONSTRUCTION})} />
                         <VerticalBtn label="Saúde" icon={Globe} active={formData.vertical === AppVertical.MEDICAL} onClick={() => setFormData({...formData, vertical: AppVertical.MEDICAL})} />
                         <VerticalBtn label="Varejo" icon={Layers} active={formData.vertical === AppVertical.RETAIL} onClick={() => setFormData({...formData, vertical: AppVertical.RETAIL})} />
                      </div>
                    </div>
                 </section>

                 <section className="space-y-6">
                    <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] border-b border-gray-50 pb-3 flex items-center gap-2 italic">
                       <MapPin size={16} /> Endereço de Operação
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                      <div className="md:col-span-1">
                        <InputGroup label="CEP" value={formData.zip} onChange={(v: string) => setFormData({...formData, zip: v})} placeholder="00000-000" />
                      </div>
                      <div className="md:col-span-3">
                        <InputGroup label="Logradouro" value={formData.address} onChange={(v: string) => setFormData({...formData, address: v})} placeholder="Rua, Avenida, Praça..." />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                      <InputGroup label="Bairro" value={formData.neighborhood} onChange={(v: string) => setFormData({...formData, neighborhood: v})} placeholder="Ex: Campolim" />
                      <InputGroup label="Cidade" value={formData.city} onChange={(v: string) => setFormData({...formData, city: v})} placeholder="Sorocaba" />
                      <InputGroup label="Estado (UF)" value={formData.state} onChange={(v: string) => setFormData({...formData, state: v.toUpperCase()})} maxLength={2} />
                    </div>
                 </section>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-10 animate-in slide-in-from-right-4 duration-500">
                 <div className="text-center space-y-2 mb-10">
                    <h3 className="text-2xl font-black uppercase text-secondary italic">Plano Selecionado</h3>
                    <p className="text-gray-400 text-sm font-medium">Verifique os limites antes de prosseguir.</p>
                 </div>
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <PlanCard title="STARTER" price="597" active={formData.plan === 'START'} onClick={() => setFormData({...formData, plan: 'START'})} features={['Até 100 Ativos', '5 Operadores', 'Gestão O.S.']} />
                    <PlanCard title="PRO" price="797" active={formData.plan === 'PRO'} onClick={() => setFormData({...formData, plan: 'PRO'})} features={['Até 250 Ativos', '15 Operadores', 'IA Vision Ativa', 'NFS-e Sincronizada']} />
                    <PlanCard title="ENTERPRISE" price="1497" active={formData.plan === 'ENTERPRISE'} onClick={() => setFormData({...formData, plan: 'ENTERPRISE'})} features={['Ativos Ilimitados', 'Operadores Ilimitados', 'Consultoria VIP', 'API Master']} />
                 </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-10 animate-in slide-in-from-right-4 duration-500">
                 <section className="space-y-8">
                    <div className="bg-primary/5 p-8 rounded-[40px] border-2 border-dashed border-primary/20 text-center space-y-4">
                       <div className="w-20 h-20 bg-primary text-white rounded-3xl flex items-center justify-center mx-auto shadow-xl">
                          <UserCheck size={40} />
                       </div>
                       <h3 className="text-2xl font-black uppercase text-secondary italic leading-none">Acesso do Administrador</h3>
                       <p className="text-[10px] text-primary font-bold uppercase tracking-widest">Este será o login master da unidade</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                       <InputGroup label="Nome do Gestor" value={formData.managerName} onChange={(v: string) => setFormData({...formData, managerName: v})} placeholder="Nome Completo" />
                       <InputGroup label="WhatsApp do Gestor" value={formData.whatsapp} onChange={(v: string) => setFormData({...formData, whatsapp: v})} placeholder="(00) 00000-0000" />
                       <div className="md:col-span-2">
                          <InputGroup label="E-mail de Acesso (Login)" value={formData.email} onChange={(v: string) => setFormData({...formData, email: v})} placeholder="gestor@empresa.com.br" type="email" />
                       </div>
                       <div className="md:col-span-2">
                          <InputGroup label="Senha de Acesso" value={formData.password} onChange={(v: string) => setFormData({...formData, password: v})} type="password" placeholder="Mínimo 8 caracteres" />
                       </div>
                    </div>
                 </section>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-10 animate-in slide-in-from-right-4 duration-500">
                 <div className="flex justify-between items-center border-b pb-4">
                    <div>
                      <h3 className="text-2xl font-black uppercase text-secondary italic">Ativar Abas Operacionais</h3>
                      <p className="text-gray-400 text-sm font-medium">Escolha quais módulos o gestor poderá acessar.</p>
                    </div>
                    <Badge className="bg-primary text-white border-none font-black text-xs px-4 py-2">{formData.authorizedModules.length} MÓDULOS</Badge>
                 </div>

                 <div className="grid grid-cols-1 gap-12 pb-20">
                    {groups.map(group => (
                      <div key={group} className="space-y-4">
                         <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                            <div className="text-[10px] font-black uppercase tracking-[0.3em] flex items-center gap-2 text-primary">
                               <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></div> {group}
                            </div>
                            <button type="button" onClick={() => selectModulesByGroup(group)} className="text-[9px] font-black text-slate-400 uppercase tracking-widest hover:text-primary transition-colors">
                               Alternar Grupo
                            </button>
                         </div>
                         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {ALL_UNIT_MODULES.filter(m => m.group === group).map(mod => (
                              <button
                                key={mod.id}
                                type="button"
                                onClick={() => toggleModule(mod.id)}
                                className={`flex items-center justify-between p-5 rounded-2xl border-2 transition-all group/mod ${
                                  formData.authorizedModules.includes(mod.id)
                                    ? 'bg-primary border-primary text-white shadow-lg'
                                    : 'bg-white border-gray-100 text-gray-400 hover:border-primary/20 shadow-sm'
                                }`}
                              >
                                 <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-xl transition-colors ${formData.authorizedModules.includes(mod.id) ? 'bg-white/20 text-white' : 'bg-gray-100'}`}>
                                       <mod.icon size={18} strokeWidth={2.5} />
                                    </div>
                                    <span className={`text-[10px] font-black uppercase tracking-tight ${formData.authorizedModules.includes(mod.id) ? 'text-white' : 'text-slate-500'}`}>{mod.label}</span>
                                 </div>
                                 <div className={`w-5 h-5 rounded-full flex items-center justify-center border-2 ${formData.authorizedModules.includes(mod.id) ? 'bg-white text-primary border-white' : 'border-gray-100'}`}>
                                    {formData.authorizedModules.includes(mod.id) && <Check size={12} strokeWidth={4} />}
                                 </div>
                              </button>
                            ))}
                         </div>
                      </div>
                    ))}
                 </div>
              </div>
            )}
          </div>

          <div className="p-10 border-t border-gray-100 bg-gray-50 flex justify-between items-center shrink-0">
             <button type="button" onClick={step === 1 ? onClose : () => setStep(step - 1)} className="px-10 py-5 rounded-[24px] font-black uppercase text-[11px] tracking-widest text-gray-400 hover:text-secondary">
               {step === 1 ? 'Cancelar' : 'Voltar Etapa'}
             </button>
             
             {step < 4 ? (
               <button 
                type="button" 
                onClick={handleNextStep} 
                className="bg-primary text-white px-12 py-5 rounded-[24px] font-black uppercase text-[11px] tracking-widest shadow-xl shadow-primary/20 flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all"
               >
                 Próximo Passo <CheckCircle2 size={18} />
               </button>
             ) : (
               <button 
                type="button" 
                onClick={handleFinalSubmit} 
                className="bg-emerald-600 text-white px-16 py-5 rounded-[24px] font-black uppercase text-xs tracking-widest shadow-2xl hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-3"
               >
                 <Save size={22} /> Provisionar Unidade Agora
               </button>
             )}
          </div>
        </div>
      </div>
    </div>
  );
};

// StepIndicator definition
const StepIndicator = ({ num, label, active, done }: any) => (
  <div className={`flex items-center gap-2 transition-all ${active ? 'opacity-100 scale-110' : done ? 'opacity-100' : 'opacity-30'}`}>
     <div className={`w-6 h-6 rounded-lg flex items-center justify-center font-black text-[10px] ${active ? 'bg-primary text-white shadow-lg shadow-primary/20' : done ? 'bg-emerald-50 text-white' : 'bg-white/10 text-white'}`}>
        {done ? '✓' : num}
     </div>
     <span className="text-[9px] font-black uppercase tracking-widest text-white/60 hidden sm:block">{label}</span>
  </div>
);

const VerticalBtn = ({ label, icon: Icon, active, onClick }: any) => (
  <button type="button" onClick={onClick} className={`p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all ${active ? 'bg-primary border-primary text-white shadow-lg' : 'bg-white border-gray-100 text-gray-400 hover:border-gray-200 shadow-sm'}`}>
     <Icon size={20} />
     <span className="text-[9px] font-black uppercase tracking-widest">{label}</span>
  </button>
);

const PlanCard = ({ title, price, active, onClick, features }: any) => (
  <div onClick={onClick} className={`p-8 rounded-[36px] border-4 transition-all cursor-pointer flex flex-col gap-6 ${active ? 'bg-white border-primary shadow-2xl scale-105' : 'bg-gray-50 border-transparent opacity-60 hover:opacity-100'}`}>
     <div className="text-center">
        <h4 className="text-lg font-black uppercase tracking-tight text-secondary">{title}</h4>
        <div className="flex items-center justify-center gap-1 mt-2">
           <span className="text-[10px] font-black text-gray-400">R$</span>
           <span className="text-4xl font-black text-secondary tracking-tighter">{price}</span>
        </div>
     </div>
     <div className="space-y-2 flex-1">
        {features.map((f: string) => (
           <div key={f} className="flex items-center gap-2">
              <CheckCircle2 size={12} className="text-emerald-500" />
              <span className="text-[10px] font-bold text-gray-600 uppercase tracking-tight">{f}</span>
           </div>
        ))}
     </div>
     {active && <div className="text-[10px] font-black text-primary uppercase tracking-widest text-center mt-2">SELECIONADO</div>}
  </div>
);

const InputGroup = ({ label, value, onChange, placeholder, type = "text", maxLength }: any) => (
  <div className="space-y-2">
    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">{label}</label>
    <input 
      type={type} 
      maxLength={maxLength} 
      className="w-full bg-gray-50 border border-gray-100 p-5 rounded-[24px] outline-none font-bold text-secondary focus:ring-4 focus:ring-primary/5 focus:border-primary transition-all shadow-sm placeholder:text-gray-300" 
      placeholder={placeholder} 
      value={value} 
      onChange={e => onChange(e.target.value)} 
    />
  </div>
);
